import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type AppRole = "primary_owner" | "owner" | "agent" | "user";

interface ImpersonationState {
  active: boolean;
  logId: string | null;
  targetUserId: string | null;
  targetRole: AppRole | null;
  targetDisplayName: string | null;
  targetAvatarUrl: string | null;
  targetEmail: string | null;
  targetTrialExpiresAt: string | null;
  targetSubscriptionPlan: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  displayName: string | null;
  avatarUrl: string | null;
  trialExpiresAt: string | null;
  subscriptionPlan: string | null;
  accountStatus: string | null;
  isTrialActive: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  // Impersonation
  impersonation: ImpersonationState;
  startImpersonation: (targetUserId: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  /** True when impersonation is active — all writes should be blocked */
  isReadOnly: boolean;
  // Effective values (respects impersonation)
  effectiveRole: AppRole | null;
  effectiveDisplayName: string | null;
  effectiveAvatarUrl: string | null;
  effectiveUserId: string | null;
  effectiveTrialExpiresAt: string | null;
  effectiveSubscriptionPlan: string | null;
}

const defaultImpersonation: ImpersonationState = {
  active: false,
  logId: null,
  targetUserId: null,
  targetRole: null,
  targetDisplayName: null,
  targetAvatarUrl: null,
  targetEmail: null,
  targetTrialExpiresAt: null,
  targetSubscriptionPlan: null,
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  role: null,
  displayName: null,
  avatarUrl: null,
  trialExpiresAt: null,
  subscriptionPlan: null,
  accountStatus: null,
  isTrialActive: false,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
  impersonation: defaultImpersonation,
  startImpersonation: async () => {},
  endImpersonation: async () => {},
  isReadOnly: false,
  effectiveRole: null,
  effectiveDisplayName: null,
  effectiveAvatarUrl: null,
  effectiveUserId: null,
  effectiveTrialExpiresAt: null,
  effectiveSubscriptionPlan: null,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [trialExpiresAt, setTrialExpiresAt] = useState<string | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationState>(defaultImpersonation);

  const isTrialActive = trialExpiresAt ? new Date(trialExpiresAt) > new Date() : false;

  const fetchUserMeta = async (userId: string): Promise<AppRole | null> => {
    try {
      const [{ data: roleData }, { data: profileData }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId).maybeSingle(),
        supabase.from("profiles").select("display_name, avatar_url, trial_expires_at, subscription_plan, status").eq("user_id", userId).maybeSingle(),
      ]);

      const resolvedRole = (roleData?.role as AppRole) || null;
      setRole(resolvedRole);
      setDisplayName(profileData?.display_name || null);
      setAvatarUrl(profileData?.avatar_url || null);
      setTrialExpiresAt(profileData?.trial_expires_at || null);
      setSubscriptionPlan(profileData?.subscription_plan || null);
      setAccountStatus((profileData as any)?.status || null);
      return resolvedRole;
    } finally {
      setLoading(false);
    }
  };

  const syncImpersonationState = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("impersonate", {
        body: { action: "status" },
      });

      if (error || data?.error || !data?.active) {
        setImpersonation(defaultImpersonation);
        return;
      }

      setImpersonation({
        active: true,
        logId: data.log_id || null,
        targetUserId: data.target?.user_id || null,
        targetRole: (data.target?.role as AppRole) || null,
        targetDisplayName: data.target?.display_name || null,
        targetAvatarUrl: data.target?.avatar_url || null,
        targetEmail: data.target?.email || null,
        targetTrialExpiresAt: data.target?.trial_expires_at || null,
        targetSubscriptionPlan: data.target?.subscription_plan || null,
      });
    } catch {
      setImpersonation(defaultImpersonation);
    }
  };

  useEffect(() => {
    let mounted = true;
    let initialized = false;

    // 1. Initial session fetch — only source of truth for first load
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      setUser(s?.user ?? null);

      if (s?.user) {
        const resolvedRole = await fetchUserMeta(s.user.id);
        if (!mounted) return;
        // Check if account is disabled (sellers only)
        if (accountStatus && accountStatus.startsWith("disabled") && resolvedRole !== "primary_owner" && resolvedRole !== "owner" && resolvedRole !== "agent") {
          await supabase.auth.signOut();
          setUser(null); setSession(null); setRole(null);
          setLoading(false);
          return;
        }
        await syncImpersonationState();
        // Track login + activity
        supabase.rpc("update_last_login" as any).then(() => {});
        supabase.rpc("increment_daily_usage" as any, { _user_id: s.user.id, _is_action: false }).then(() => {});
        initialized = true;
      } else {
        setLoading(false);
        setImpersonation(defaultImpersonation);
        initialized = true;
      }
    });

    // 2. Listen only for real auth changes (sign-in, sign-out, password recovery)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted || !initialized) return;

        // Ignore background events that don't represent a real auth change
        if (event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") return;

        if (event === "SIGNED_OUT") {
          setSession(null);
          setUser(null);
          setRole(null);
          setDisplayName(null);
          setAvatarUrl(null);
          setTrialExpiresAt(null);
          setSubscriptionPlan(null);
          setAccountStatus(null);
          setImpersonation(defaultImpersonation);
          setLoading(false);
          return;
        }

        // SIGNED_IN, PASSWORD_RECOVERY, USER_UPDATED
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // Don't set loading=true on subsequent auth events to avoid remounting the dashboard
          fetchUserMeta(session.user.id).then(() => {
            if (!mounted) return;
            syncImpersonationState();
          });
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const refreshProfile = async () => {
    if (!user) return;
    await fetchUserMeta(user.id);
    await syncImpersonationState();
  };

  const startImpersonation = async (targetUserId: string) => {
    if (role !== "owner" && role !== "primary_owner" && role !== "agent") return;
    try {
      const { data, error } = await supabase.functions.invoke("impersonate", {
        body: { action: "start", target_user_id: targetUserId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setImpersonation({
        active: true,
        logId: data.log_id,
        targetUserId,
        targetRole: data.target.role as AppRole,
        targetDisplayName: data.target.display_name,
        targetAvatarUrl: data.target.avatar_url,
        targetEmail: data.target.email,
        targetTrialExpiresAt: data.target.trial_expires_at,
        targetSubscriptionPlan: data.target.subscription_plan,
      });
    } catch (err: any) {
      throw err;
    }
  };

  const endImpersonation = async () => {
    try {
      await supabase.functions.invoke("impersonate", {
        body: { action: "end", log_id: impersonation.logId },
      });
    } catch {
      // no-op
    } finally {
      setImpersonation(defaultImpersonation);
    }
  };

  const signOut = async () => {
    try {
      await supabase.functions.invoke("impersonate", {
        body: { action: "end" },
      });
    } catch {
      // no-op
    }

    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    setDisplayName(null);
    setAvatarUrl(null);
    setTrialExpiresAt(null);
    setSubscriptionPlan(null);
    setAccountStatus(null);
    setImpersonation(defaultImpersonation);
  };

  // Effective values: when impersonating, show target's data; otherwise show own
  const effectiveRole = impersonation.active ? impersonation.targetRole : role;
  const effectiveDisplayName = impersonation.active ? impersonation.targetDisplayName : displayName;
  const effectiveAvatarUrl = impersonation.active ? impersonation.targetAvatarUrl : avatarUrl;
  const effectiveUserId = impersonation.active ? impersonation.targetUserId : user?.id || null;
  const effectiveTrialExpiresAt = impersonation.active ? impersonation.targetTrialExpiresAt : trialExpiresAt;
  const effectiveSubscriptionPlan = impersonation.active ? impersonation.targetSubscriptionPlan : subscriptionPlan;

  const isReadOnly = impersonation.active;

  return (
    <AuthContext.Provider value={{
      user, session, role, displayName, avatarUrl, trialExpiresAt, subscriptionPlan, accountStatus, isTrialActive,
      loading, signOut, refreshProfile,
      impersonation, startImpersonation, endImpersonation, isReadOnly,
      effectiveRole, effectiveDisplayName, effectiveAvatarUrl, effectiveUserId,
      effectiveTrialExpiresAt, effectiveSubscriptionPlan,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
